import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

class AlidhaarProduct extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }

  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
    this.catalog = JSON.parse(this.querySelector('[data-catalog]').textContent);
    this.products = new Map(this.catalog.map(product => [product.id, product]));
    this.variant = this.dataset.currentVariant;
    this.size = 1;
    this.selection = [this.variant, '', ''];
    this.busy = false;
    this.form = this.querySelector('form');
    this.form.addEventListener('submit', event => this.add(event), options);
    this.querySelector('[data-single-variant]')?.addEventListener('change', event => {
      this.variant = event.target.value;
      this.selection[0] = this.variant;
      const product = this.products.get(this.variant);
      if (product) this.querySelector('[data-product-price]').textContent = this.money(product.price);
      this.buildRows();
      this.render();
    }, options);
    this.querySelectorAll('[data-photo]').forEach(button => button.addEventListener('click', () => {
      const image = this.querySelector('[data-main-photo] img');
      if (!image) return;
      image.removeAttribute('srcset');
      image.src = button.dataset.photo;
      image.style.transform = `scale(${button.dataset.zoom})`;
      image.style.transformOrigin = button.dataset.origin || '50% 50%';
      this.querySelectorAll('[data-photo]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    }, options));
    this.querySelectorAll('[name="set_size"]').forEach(input => input.addEventListener('change', () => {
      this.size = Number(input.value);
      this.buildRows();
      this.render();
      if (this.size > 1) this.loadMap();
    }, options));
    const setUi = this.querySelector('[data-set-ui]');
    if (setUi) setUi.hidden = false;
    this.render();
  }

  money(cents) {
    const currency = this.dataset.currency || 'PKR';
    if (currency === 'PKR') return `Rs. ${new Intl.NumberFormat('en-PK', { maximumFractionDigits: 2 }).format(cents / 100)}`;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
  }

  async loadMap() {
    if (this.map || this.loading) return;
    this.loading = true;
    this.mapError = false;
    this.render();
    try {
      const response = await fetch(this.dataset.map, { signal: this.controller.signal });
      if (!response.ok) throw Error();
      this.map = await response.json();
    } catch (error) {
      if (error.name !== 'AbortError') this.mapError = true;
    } finally {
      this.loading = false;
      if (this.isConnected) this.render();
    }
  }

  buildRows() {
    const container = this.querySelector('[data-selection-rows]');
    if (!container) return;
    container.replaceChildren();
    for (let index = 0; index < this.size && this.size > 1; index++) {
      const row = document.createElement('label');
      row.className = 'ald-pdp__selection';
      const image = document.createElement('img');
      image.width = 36;
      image.height = 48;
      image.alt = '';
      image.dataset.selectionImage = '';
      const name = document.createElement('span');
      name.textContent = `${this.strings.perfume} ${index + 1}`;
      const select = document.createElement('select');
      select.setAttribute('aria-label', name.textContent);
      select.add(new Option(this.strings.choose, ''));
      for (const product of this.catalog) {
        const title = product.variant === 'Default Title' ? product.name : `${product.name} · ${product.variant}`;
        const option = new Option(`${title} — ${this.money(product.price)}`, product.id);
        option.disabled = !product.available || product.min > 1;
        select.add(option);
      }
      select.value = this.selection[index];
      select.addEventListener('change', () => {
        this.selection[index] = select.value;
        this.render();
      }, { signal: this.controller.signal });
      row.append(image, name, select);
      container.append(row);
    }
  }

  state() {
    if (this.size === 1) {
      const product = this.products.get(this.variant);
      return { ready: !!product?.available, id: this.variant, total: product?.price || 0, quantity: product?.min || 1, message: product?.available ? '' : this.strings.soldOut };
    }
    const ids = this.selection.slice(0, this.size);
    const products = ids.map(id => this.products.get(id));
    const complete = products.every(product => product?.available && product.min === 1);
    const sum = products.reduce((total, product) => total + (product?.price || 0), 0);
    const bundle = this.map?.[ids.slice().sort().join(':')];
    const mapped = complete && bundle?.size === this.size && bundle.compare === sum;
    const available = this.dataset[this.size === 2 ? 'duetAvailable' : 'trioAvailable'] === 'true';
    const ready = mapped && available;
    const message = this.loading ? this.strings.loading : this.mapError ? this.strings.error : !complete ? this.strings.choose : !ready ? this.strings.unavailable : '';
    return { ready, id: bundle?.id, total: mapped ? bundle.price : 0, sum, complete, mapped, quantity: 1, message };
  }

  render() {
    const state = this.state();
    const selected = this.querySelector('[data-set-selection]');
    if (selected) selected.hidden = this.size === 1;
    const fineprint = this.querySelector('[data-set-fineprint]');
    if (fineprint) fineprint.hidden = this.size === 1;
    this.querySelectorAll('.ald-pdp__selection').forEach((row, index) => {
      const product = this.products.get(this.selection[index]);
      const image = row.querySelector('img');
      if (product?.image) { image.src = product.image; image.hidden = false; } else { image.removeAttribute('src'); image.hidden = true; }
    });
    this.querySelectorAll('.ald-pdp__choice').forEach(card => {
      card.querySelectorAll('.ald-pdp__bottles img').forEach((image, index) => {
        const product = this.products.get(this.selection[index]) || this.products.get(this.variant);
        if (product?.image) { image.removeAttribute('srcset'); image.src = product.image; }
      });
    });
    if (this.size > 1) {
      this.querySelector('[data-original]').textContent = state.mapped ? this.money(state.sum) : '';
      this.querySelector('[data-total]').textContent = state.mapped ? this.money(state.total) : '—';
      this.querySelector('[data-saving]').textContent = state.mapped ? `${this.strings.saving} ${this.money(state.sum - state.total)}` : '';
    }
    this.querySelector('[data-status]').textContent = this.busy ? this.strings.busy : state.message;
    const button = this.querySelector('[data-add]');
    button.disabled = this.busy || !state.ready;
    button.setAttribute('aria-busy', String(this.busy));
    const label = this.busy ? this.strings.busy : this.size === 1 ? (state.ready ? this.strings.single : this.strings.soldOut) : this.strings.set;
    this.querySelector('[data-add-text]').textContent = `${label}${this.size > 1 && state.mapped ? ` — ${this.money(state.total)}` : ''}`;
    this.querySelectorAll('select, [name="set_size"]').forEach(control => { control.disabled = this.busy; });
  }

  async add(event) {
    event.preventDefault();
    const state = this.state();
    if (this.busy || !state.ready) return;
    this.busy = true;
    this.render();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    document.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(state.id), quantity: state.quantity }], promise: deferred.promise }));
    let accepted = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(element => element.dataset.sectionId))].slice(0, 5);
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(state.id), quantity: state.quantity }], sections, sections_url: location.pathname }) });
      const result = await response.json();
      if (!response.ok || result.status) throw Error(result.description || this.strings.error);
      accepted = true;
      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`);
      if (!cartResponse.ok) throw Error();
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'product-form-component', sourceId: this.form.id, itemCount: state.quantity, sections: result.sections, didError: false } });
      if (!document.querySelector('cart-drawer-component[auto-open]')) location.assign(Theme.routes.cart_url || `${Shopify.routes.root}cart`);
    } catch (error) {
      deferred.reject(error);
      if (accepted) { location.assign(`${Shopify.routes.root}cart`); return; }
      this.busy = false;
      this.render();
      this.querySelector('[data-status]').textContent = error.message || this.strings.error;
      return;
    }
    this.busy = false;
    this.render();
  }
}

if (!customElements.get('alidhaar-product')) customElements.define('alidhaar-product', AlidhaarProduct);
